import React, { useState } from 'react';
import { Col, Row, Table, FormControl } from 'react-bootstrap';
import { ArrowDownCircle, ArrowUpCircle, ArrowClockwise, PlusCircleFill, Trash2Fill } from 'react-bootstrap-icons';
import TextButton from '../components/TextButton';
import { normalizeId } from '../form-generator/helpers/utility';

const getDatalistOptions = dropdownOptions =>
  (dropdownOptions || []).map(option => (Array.isArray(option) ? option[0] : option)).filter(Boolean);

const ControlledTableInput = ({
  id,
  title,
  tableFields = [],
  value = [],
  onChange,
  disabled = false,
  dropdownOptions = [],
  defaultHideOptions = true,
  showHideToggle = true,
  showResetToDefault = false,
  onResetToDefault,
  size = 'sm'
}) => {
  const [hideComponent, setHideComponent] = useState(defaultHideOptions);
  const rows = Array.isArray(value) ? value : [];
  const datalistOptions = getDatalistOptions(dropdownOptions);

  const updateRow = (rowIndex, fieldIndex, fieldValue) => {
    const nextRows = rows.map((row, index) => {
      if (index !== rowIndex) return [...row];
      const nextRow = [...(row || [])];
      nextRow[fieldIndex] = fieldValue;
      return nextRow;
    });
    onChange(nextRows);
  };

  const addRow = () => {
    onChange([...rows, tableFields.map(() => '')]);
  };

  const removeRow = rowIndex => {
    onChange(rows.filter((_, index) => index !== rowIndex));
  };

  const renderField = (field, rowIndex, fieldIndex, cellValue) => {
    const inputId = normalizeId(`${id}-${field.key || field.title}-${rowIndex}`);

    if (field.type === 'datalist-text') {
      return (
        <>
          <FormControl
            size="sm"
            type="text"
            id={inputId}
            list={`${inputId}-list`}
            value={cellValue ?? ''}
            disabled={disabled}
            style={{ fontSize: '10px', height: '25px' }}
            onChange={e => updateRow(rowIndex, fieldIndex, e.target.value)}
          />
          <datalist id={`${inputId}-list`}>
            {datalistOptions.map(option => (
              <option key={option} value={option} />
            ))}
          </datalist>
        </>
      );
    }

    if (field.type === 'number') {
      return (
        <FormControl
          size="sm"
          type="number"
          id={inputId}
          value={cellValue ?? ''}
          disabled={disabled}
          style={{ fontSize: '10px' }}
          onChange={e => updateRow(rowIndex, fieldIndex, e.target.value)}
        />
      );
    }

    return (
      <FormControl
        size="sm"
        type="text"
        id={inputId}
        value={cellValue ?? ''}
        disabled={disabled}
        style={{ fontSize: '10px' }}
        onChange={e => updateRow(rowIndex, fieldIndex, e.target.value)}
      />
    );
  };

  return (
    <>
      {title ? <h6 className="mb-2 smallFont px-3">{title}</h6> : null}
      <div className={`${hideComponent ? 'd-none' : ''} p-3${title ? ' pt-0' : ''}`}>
        <Table id={id} bordered className="rounded" responsive>
          <thead>
            <tr className="bg-primary text-white smallFont">
              {[...tableFields, { title: 'Action' }].map((field, index) => (
                <th key={index} className="text-center py-1 px-0">
                  {field.title}
                </th>
              ))}
            </tr>
          </thead>
          {rows.length > 0 && (
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {tableFields.map((field, fieldIndex) => (
                    <td key={fieldIndex} className={`${size === 'sm' ? 'p-1' : ''} text-center`}>
                      {renderField(field, rowIndex, fieldIndex, row?.[fieldIndex])}
                    </td>
                  ))}
                  <td className={`${size === 'sm' ? 'p-1' : ''} text-center`}>
                    <Trash2Fill
                      className={`smallFont ${disabled ? 'text-muted' : 'text-danger pointer'}`}
                      onClick={e => (disabled ? e.stopPropagation() : removeRow(rowIndex))}
                      title="Delete This Row"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          )}
          {rows.length === 0 && (
            <tfoot>
              <tr>
                <td className={`mid text-center ${size === 'sm' ? 'p-1' : ''}`} colSpan={tableFields.length + 1}>
                  Nothing to show
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
        {!disabled && (
          <Row className="text-right">
            <Col xs={12}>
              {showResetToDefault && onResetToDefault && (
                <TextButton
                  Icon={ArrowClockwise}
                  variant="primary"
                  text="Reset to Default"
                  onClick={onResetToDefault}
                  className="mr-3"
                />
              )}
              <TextButton Icon={PlusCircleFill} variant="success" text="Add A Row" onClick={addRow} />
            </Col>
          </Row>
        )}
      </div>
      {showHideToggle && (
        <h6
          className="hover-underline smallFont text-primary mb-2 px-3"
          onClick={() => setHideComponent(!hideComponent)}
        >
          {hideComponent ? (
            <>
              Show Options <ArrowDownCircle size={13} />
            </>
          ) : (
            <>
              Hide Options <ArrowUpCircle size={13} />
            </>
          )}
        </h6>
      )}
    </>
  );
};

export default ControlledTableInput;
